import { invalidateBffSession, isBffMode, probeBffSession, readBffUserId, redirectToBffLogin } from './bff';

// focus/visibilitychange стреляют пачками при alt-tab — чаще, чем меняется владелец куки.
const RECHECK_COOLDOWN_MS = 5_000;
// Уход, отменённый диалогом beforeunload («Остаться»), оставляет вкладку жить: защёлка живёт ровно
// столько, чтобы состоявшаяся навигация убрала документ, иначе отмена выключила бы сторож навсегда.
const LEAVE_SETTLE_MS = 3_000;

let installed = false;
let knownUserId: string | null = null;
let lastCheckAt = 0;
let leaving = false;
let leaveLatchTimer: ReturnType<typeof setTimeout> | null = null;
let signedInSurface: () => boolean = () => true;

// jsdom запрещает подменять location — швы существуют только ради спеков.
let reload: () => void = () => window.location.reload();
let goToLogin: (returnUrl: string) => void = returnUrl => redirectToBffLogin(returnUrl);

export function resetSessionWatchForTests(): void {
  installed = false;
  knownUserId = null;
  lastCheckAt = 0;
  leaving = false;
  if (leaveLatchTimer) clearTimeout(leaveLatchTimer);
  leaveLatchTimer = null;
  signedInSurface = () => true;
  reload = () => window.location.reload();
  goToLogin = returnUrl => redirectToBffLogin(returnUrl);
}

export function setSessionWatchNavigationForTests(fns: { reload: () => void; goToLogin: (returnUrl: string) => void }): void {
  reload = fns.reload;
  goToLogin = fns.goToLogin;
}

/**
 * Выход или смена аккаунта в соседнем приложении этого браузера снимает сессию и здесь, но вкладка,
 * вернувшаяся в фокус, узнала бы об этом лишь на первом запросе — до него она показывает прежнего
 * пользователя. Ре-чек на фокусе сверяет сессию с сервером: снята — на вход, другой владелец куки —
 * ре-бут вкладки.
 *
 * signedInSurface — «текущий экран живёт под входом». Страница без входа (клиентская ссылка), куда
 * вкладка ушла SPA-переходом из кабинета, по снятой сессии на вход не уводится.
 */
export function installSessionWatch(options?: { signedInSurface?: () => boolean }): void {
  if (installed) return;
  installed = true;
  signedInSurface = options?.signedInSurface ?? (() => true);
  window.addEventListener('focus', () => void recheckSession());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void recheckSession();
  });
}

export async function recheckSession(): Promise<void> {
  if (!installed || leaving || !isBffMode() || !signedInSurface()) return;
  // Вкладка без живой сессии (страница до входа, гостевой экран) уводить на вход не должна.
  knownUserId ??= readBffUserId();
  if (!knownUserId) return;
  const now = Date.now();
  if (now - lastCheckAt < RECHECK_COOLDOWN_MS) return;
  lastCheckAt = now;

  invalidateBffSession();
  const verdict = await probeBffSession();
  // Пока проба летела, вкладка могла уйти SPA-переходом на страницу без входа.
  if (leaving || !signedInSurface()) return;
  // 'unknown' (сеть, не-200) — fail-open: вкладка живёт, пока сервер не скажет, что сессии нет.
  if (verdict === 'dead') {
    latchLeaving();
    goToLogin(`${window.location.pathname}${window.location.search}`);
    return;
  }

  const current = readBffUserId();
  if (verdict === 'alive' && current && current !== knownUserId) {
    latchLeaving();
    reload();
  }
}

function latchLeaving(): void {
  leaving = true;
  if (leaveLatchTimer) clearTimeout(leaveLatchTimer);
  leaveLatchTimer = setTimeout(() => {
    leaving = false;
    leaveLatchTimer = null;
  }, LEAVE_SETTLE_MS);
}
